import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Alert, Box, Button, Skeleton, styled } from "@mui/material";
import { toast } from "react-hot-toast";
import { Markdown } from "../discover/markdown";
import { useGenerateCommsContext } from "./generate-comms-context";

import type { FC } from "react";

const Content = styled(Box)(({ theme }) => ({
  whiteSpace: "pre-line",
  maxWidth: theme.breakpoints.values.md,

  p: {
    margin: theme.spacing(0),
  },
  ul: {
    margin: theme.spacing(0),
  },
  li: {
    margin: theme.spacing(0),
  },
}));

export const CommsMarkdownContent: FC<{ tone: string; format: string }> = ({ tone, format }) => {
  const loader = useGenerateCommsContext().text.get(tone);

  if (!loader || loader.loading) return <SkeletonText />;

  if (loader?.error)
    return <Alert severity="warning">Content generation failed. Please try again.</Alert>;

  return (
    <Content sx={{ p: 0, m: 0 }}>
      {loader.data ? (
        <>
          <Markdown content={loader.data.text} />
          {format.toLowerCase() !== "communication" && (
            <Button
              variant="outlined"
              startIcon={<ContentCopyOutlinedIcon />}
              data-analytics-id="comms-content-copy"
              onClick={() => {
                void navigator.clipboard.writeText(loader.data.text);
                toast.success("Text copied");
              }}
            >
              Copy text
            </Button>
          )}
        </>
      ) : (
        <>
          <Skeleton variant="text" width="50%" sx={{ mb: 2 }} />
          {Array.from<number>({ length: 6 })
            .fill(0)
            .map((_, i) => (
              // oxlint-disable-next-line react/no-array-index-key -- Skeleton placeholders have no identity
              <Skeleton key={i} variant="text" />
            ))}
          <Skeleton variant="text" width="20%" sx={{ mt: 2 }} />
          <Skeleton variant="text" width="50%" />
        </>
      )}
    </Content>
  );
};

const SkeletonText: FC<{ lines?: number }> = ({ lines = 5 }) => (
  <>
    {Array.from<number>({ length: lines })
      .fill(0)
      .map((_, i) => (
        // oxlint-disable-next-line react/no-array-index-key -- Skeleton placeholders have no identity
        <Skeleton key={i} variant="text" />
      ))}
  </>
);
